import React, { useState, useEffect, useCallback } from 'react';
import {
    Grid, Button, Typography, Tabs, Tab, Box, Paper,
    Table, TableHead, TableBody, TableRow, TableCell,
    TableContainer, TextField, InputAdornment, Chip,
    IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogContentText, DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import API from '../../utils/Axios.utils';
import { Notification } from '../../utils/Notification';
import useAuth from '../../hooks/useAuth';
import CreateRequisitionModal from './CreateRequisitionModal';
import ReviewRequisitionModal from './ReviewRequisitionModal';
import ViewRequisitionModal from './ViewRequisitionModal';
import CustomModel from '../../utils/CustomModel';
import { useQueryClient } from '@tanstack/react-query';

// KPI card
const KpiCard = ({ label, value, color }) => (
    <Paper elevation={2} sx={{
        p: 2.5, borderRadius: 2, borderLeft: `5px solid ${color}`,
        minHeight: 90, display: 'flex', flexDirection: 'column', justifyContent: 'center',
    }}>
        <Typography variant="h4" fontWeight={700} color={color}>{value ?? '—'}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{label}</Typography>
    </Paper>
);

// Status chip colour mapping
const statusColor = (status) => {
    switch (status) {
        case 'approved': return 'success';
        case 'rejected': return 'error';
        case 'job_created': return 'primary';
        default: return 'warning';
    }
};

const HeadcountRequisition = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user: currentUser } = useAuth();
    const currentUserEmail = (currentUser?.email || '').toLowerCase();

    const [activeTab, setActiveTab] = useState(0);
    const [requisitions, setRequisitions] = useState([]);
    const [stats, setStats] = useState({});
    const [clients, setClients] = useState([]);
    const [searchText, setSearchText] = useState('');

    const [createOpen, setCreateOpen] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [selectedReq, setSelectedReq] = useState(null);

    const [viewOpen, setViewOpen] = useState(false);
    const [viewReq, setViewReq] = useState(null);
    const [editReq, setEditReq] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteReq, setDeleteReq] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [alert, setAlert] = useState(false);
    const [alertContent, setAlertContent] = useState('');
    const [alertType, setAlertType] = useState('');

    const fetchAll = useCallback(async () => {
        try {
            const [reqRes, statsRes] = await Promise.all([
                API.get('/api/requisitions/all'),
                API.get('/api/requisitions/stats'),
            ]);
            setRequisitions(reqRes?.data?.requisitions || []);
            setStats(statsRes?.data || {});
        } catch (err) {
            console.error('Failed to fetch requisitions:', err);
        }
    }, []);

    const fetchClients = useCallback(async () => {
        try {
            const res = await API.get('/api/client/client_list');
            setClients(res?.data?.clients || res?.data || []);
        } catch (err) {
            console.error('Failed to fetch clients:', err);
        }
    }, []);

    useEffect(() => {
        fetchAll();
        fetchClients();
    }, [fetchAll, fetchClients]);

    // Filter rows by search text
    const lowerSearch = searchText.toLowerCase();
    const filterRow = (r) =>
        !searchText ||
        r.role_title?.toLowerCase().includes(lowerSearch) ||
        r.client_name?.toLowerCase().includes(lowerSearch) ||
        r.department?.toLowerCase().includes(lowerSearch) ||
        r.requisition_id?.toLowerCase().includes(lowerSearch);

    const pendingRows = requisitions.filter((r) => r.status === 'pending_approval' && filterRow(r));
    const approvedRows = requisitions.filter((r) => r.status === 'approved' && filterRow(r));
    const allRows = requisitions.filter(filterRow);

    const currentRows = activeTab === 0 ? pendingRows : activeTab === 1 ? approvedRows : allRows;

    const handleView = (req) => {
        setViewReq(req);
        setViewOpen(true);
    };

    const handleEdit = (req) => {
        setEditReq(req);
        setCreateOpen(true);
    };

    const handleDeleteClick = (req) => {
        setDeleteReq(req);
        setDeleteOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!deleteReq) return;
        setDeleteLoading(true);
        try {
            await API.delete(`/api/requisitions/${deleteReq.id}`);
            setAlertContent(`Requisition ${deleteReq.requisition_id} deleted successfully.`);
            setAlertType('success');
            setAlert(true);
            setDeleteOpen(false);
            setDeleteReq(null);
            queryClient.invalidateQueries({ queryKey: ['candidates'] });
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
            queryClient.invalidateQueries({ queryKey: ['jobCodes'] });
            fetchAll();
        } catch (err) {
            setAlertContent(err?.response?.data?.message || 'Failed to delete requisition.');
            setAlertType('error');
            setAlert(true);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCloseCreate = () => {
        setCreateOpen(false);
        setEditReq(null);
    };

    const handleReview = (req) => { setSelectedReq(req); setReviewOpen(true); };

    const handleCreateJob = (req) => {
        navigate('/jobs/addJob', {
            state: {
                requisition: {
                    id: req.id,
                    requisition_id: req.requisition_id,
                    title: req.role_title,
                    client_id: req.client_id,
                    no_of_requirements: req.no_of_people,
                    budget: req.comp_band_max || req.comp_band_min || 0,
                    min_budget: req.comp_band_min || 0,
                    max_budget: req.comp_band_max || 0,
                    notes: req.notes,
                },
            },
        });
    };

    const columns = [
        'REQ ID', 'Role Title', 'Client', 'Department', 'Seats', 'Salary Range', 'Priority', 'Status', 'Created By', 'Approved By', 'Actions',
    ];

    const renderRow = (row) => (
        <TableRow key={row.id} hover>
            <TableCell>{row.requisition_id}</TableCell>
            <TableCell>{row.role_title}</TableCell>
            <TableCell>{row.client_name || '—'}</TableCell>
            <TableCell>{row.department}</TableCell>
            <TableCell>{row.no_of_people}</TableCell>
            <TableCell>{row.comp_band_min || 0} – {row.comp_band_max || 0}</TableCell>
            <TableCell>
                <Chip label={row.priority} color={row.priority === 'High' ? 'error' : 'default'} size="small" variant="outlined" />
            </TableCell>
            <TableCell>
                <Chip label={row.status.replace(/_/g, ' ')} color={statusColor(row.status)} size="small" />
            </TableCell>
            <TableCell>
                <Tooltip title={row.creator_email || '—'}>
                    <span>{row.creator_first_name ? `${row.creator_first_name} ${row.creator_last_name}` : row.creator_email || '—'}</span>
                </Tooltip>
            </TableCell>
            <TableCell>
                <Tooltip title={row.approver_email || '—'}>
                    <span>{row.approver_first_name ? `${row.approver_first_name} ${row.approver_last_name}` : row.approver_email || '—'}</span>
                </Tooltip>
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <Tooltip title="View Details">
                    <IconButton size="small" color="primary" onClick={() => handleView(row)}>
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Edit Requisition">
                    <IconButton size="small" color="info" onClick={() => handleEdit(row)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete Requisition">
                    <IconButton size="small" color="error" onClick={() => handleDeleteClick(row)}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {row.status === 'pending_approval' && (row.creator_email || '').toLowerCase() !== currentUserEmail && (
                    <Button size="small" variant="outlined" sx={{ ml: 1 }} onClick={() => handleReview(row)}>Review</Button>
                )}
                {row.status === 'pending_approval' && (row.creator_email || '').toLowerCase() === currentUserEmail && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>Awaiting Review</Typography>
                )}
                {row.status === 'approved' && (
                    <Button size="small" variant="contained" sx={{ ml: 1 }} onClick={() => handleCreateJob(row)}>Create Job</Button>
                )}
            </TableCell>
        </TableRow>
    );

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Grid item>
                    <Typography variant="h5" fontWeight={700}>Headcount & Requisition</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage headcount requests, approvals, and job creation.
                    </Typography>
                </Grid>
                <Grid item>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                        Create Requisition
                    </Button>
                </Grid>
            </Grid>

            {/* 3 KPI Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <KpiCard label="Total Requisitions" value={stats.total_requisitions} color="#1976d2" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <KpiCard label="Approved Requisitions" value={stats.approved_headcount} color="#2e7d32" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <KpiCard
                        label={`Pending Approvals${stats.high_priority_pending ? ` (${stats.high_priority_pending} High Priority)` : ''}`}
                        value={stats.pending_approvals}
                        color="#ed6c02"
                    />
                </Grid>
            </Grid>

            {/* Tabs + Search + Table */}
            <Paper elevation={1} sx={{ borderRadius: 2 }}>
                <Box sx={{
                    borderBottom: 1, borderColor: 'divider', px: 2, pt: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    flexWrap: 'wrap', gap: 1,
                }}>
                    <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                        <Tab label={`Pending Approvals (${pendingRows.length})`} />
                        <Tab label={`Approved Reqs (${approvedRows.length})`} />
                        <Tab label={`Headcount Planning (${allRows.length})`} />
                    </Tabs>
                    <TextField
                        size="small"
                        placeholder="Search..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                        }}
                        sx={{ width: 220 }}
                    />
                </Box>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {columns.map((col) => (
                                    <TableCell key={col} sx={{ fontWeight: 700 }}>{col}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {currentRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        No requisitions found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                currentRows.map(renderRow)
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Modals */}
            <CreateRequisitionModal
                open={createOpen}
                onClose={handleCloseCreate}
                clients={clients}
                editData={editReq}
                onCreated={fetchAll}
                setAlert={setAlert}
                setAlertContent={setAlertContent}
                setAlertType={setAlertType}
            />
            <ViewRequisitionModal
                open={viewOpen}
                onClose={() => { setViewOpen(false); setViewReq(null); }}
                requisition={viewReq}
            />
            <ReviewRequisitionModal
                open={reviewOpen}
                onClose={() => { setReviewOpen(false); setSelectedReq(null); }}
                requisition={selectedReq}
                currentUserEmail={currentUserEmail}
                onUpdated={fetchAll}
                setAlert={setAlert}
                setAlertContent={setAlertContent}
                setAlertType={setAlertType}
            />

            {/* Delete Confirmation Dialog */}
            <CustomModel
                handleDialog={() => !deleteLoading && setDeleteOpen(false)}
                open={deleteOpen}
                title="Delete Requisition"
            >
                <div
                    style={{
                        textAlign: "center",
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontSize: "24px",
                    }}
                >
                    Do you want to delete the Requisition?
                </div>
                <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "1em",
                            gap: "10px",
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleConfirmDelete}
                            disabled={deleteLoading}
                            style={{ background: "#2777AA" }}
                        >
                            Yes
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setDeleteOpen(false)}
                            disabled={deleteLoading}
                            style={{ background: "#2777AA" }}
                        >
                            NO
                        </Button>
                    </div>
                </Grid>
            </CustomModel>

            <Notification
                open={alert}
                onClose={() => setAlert(false)}
                message={alertContent}
                severity={alertType}
            />
        </Box>
    );
};

export default HeadcountRequisition;
